// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Dashboard Page
// Route: /dashboard (default landing after auth)
// apps/web/src/routes/dashboard.tsx
//
// The command center. Aggregated metrics across all projects, compression
// ratio hero, recent session activity feed, project health overview.
// CoreOrb pulsing center. The first page every user sees.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  NeonValue,
  PulseDot,
  EngineTooltip,
  MetricCard,
  LoadingShimmer,
} from '../components/engine/EnginePageShell';
import { useCounter, useSparklineData } from '../components/engine/hooks';
import { useDashboard } from '../lib/api/hooks-extended';
import { HelpBubble } from '../components/engine/HelpBubble';
import { ErrorBoundary } from '../components/engine/ErrorBoundary';

// ─── Route Definition ───────────────────────────────────────────────────────

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

// ─── Main Component ─────────────────────────────────────────────────────────

function DashboardPage() {
  const { data, loading, error, refetch } = useDashboard();

  // Animated counters
  const countCompression = useCounter(data?.overallCompressionRatio ?? 0, 1200, 400);
  const countEquivWeeks = useCounter(data?.totalEquivalentWeeks ?? 0, 900, 500);
  // Animate the raw dollar count; the format helper below picks K/M/B
  // based on magnitude so the display reads cleanly past $1M.
  const countReplCost = useCounter(
    data ? Math.round(data.totalReplacementCost ?? 0) : 0,
    1000,
    600
  );
  const countHours = useCounter(data?.totalActualHours ?? 0, 800, 700);

  // Format a dollar amount with the right unit. Past $1M switches to
  // millions (1 decimal); past $1B switches to billions (2 decimals).
  // Below $1M stays in thousands so small-tenant dashboards still
  // surface meaningful detail.
  const formatMoney = (dollars: number): string => {
    if (dollars >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(2)}B`;
    if (dollars >= 1_000_000)     return `$${(dollars / 1_000_000).toFixed(1)}M`;
    return `$${Math.round(dollars / 1000).toLocaleString()}K`;
  };
  const countSessions = useCounter(data?.totalSessions ?? 0, 700, 300);
  const countDecisions = useCounter(data?.totalDecisions ?? 0, 700, 350);
  const countWorkItems = useCounter(data?.totalWorkItems ?? 0, 700, 250);
  const countContributors = useCounter(data?.totalContributors ?? 0, 500, 200);

  // Sparkline data for decorative mini-charts
  const velocitySpark = useSparklineData(16, 10, 50);

  if (loading) {
    return (
      <EnginePageShell title="Dashboard" subtitle="COMMAND CENTER" icon="⚡" accentColor={C.neon}>
        <DashboardSkeleton />
      </EnginePageShell>
    );
  }

  if (error || !data) {
    return (
      <EnginePageShell title="Dashboard" subtitle="COMMAND CENTER" icon="⚡" accentColor={C.coral}>
        <GlassPanel accent={C.coral}>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠</div>
            <div style={{ fontSize: 16, color: C.coral, fontWeight: 600, marginBottom: 8 }}>
              Dashboard unavailable
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {error?.message || 'Unable to load metrics'}
            </div>
            <button
              onClick={refetch}
              style={{
                background: `${C.coral}20`,
                border: `1px solid ${C.coral}40`,
                color: C.coral,
                fontFamily: F.mono,
                fontSize: 12,
                padding: '8px 24px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              RETRY
            </button>
          </div>
        </GlassPanel>
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell
      title="Dashboard"
      subtitle="COMMAND CENTER"
      icon="⚡"
      accentColor={C.neon}
      statusLabel="TRACKING"
    >
      {/* ══════════ HERO — CoreOrb + Compression Ratio ══════════ */}
      <ErrorBoundary label="Compression Hero">
      <GlassPanel accent={C.neon} delay={0.1} style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '32px 36px',
            position: 'relative',
          }}
        >
          {/* CoreOrb — pulsing AI visualization */}
          <div
            style={{
              position: 'relative',
              width: 120,
              height: 120,
              flexShrink: 0,
              marginRight: 36,
            }}
          >
            {/* Outer ring */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `1px solid ${C.neon}20`,
                animation: 'engine-core-pulse 3s ease-in-out infinite',
              }}
            />
            {/* Middle ring */}
            <div
              style={{
                position: 'absolute',
                inset: 12,
                borderRadius: '50%',
                border: `1px solid ${C.neon}30`,
                animation: 'engine-core-pulse 2.5s ease-in-out infinite 0.3s',
              }}
            />
            {/* Inner core */}
            <div
              style={{
                position: 'absolute',
                inset: 28,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${C.neon}40 0%, ${C.neon}10 60%, transparent 80%)`,
                animation: 'engine-core-pulse 2s ease-in-out infinite',
                boxShadow: `0 0 40px ${C.neonGlow}`,
              }}
            />
            {/* Center dot */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: C.neon,
                boxShadow: `0 0 20px ${C.neon}, 0 0 40px ${C.neonGlow}`,
              }}
            />
            {/* Ping ring */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `2px solid ${C.neon}40`,
                animation: 'engine-ping 3s cubic-bezier(0,0,0.2,1) infinite',
              }}
            />
          </div>

          {/* Compression hero value */}
          <div style={{ flex: 1 }}>
            <HelpBubble helpKey="dashboard.compression_ratio" indicatorPosition="inline-end">
              <div
                style={{
                  fontSize: 9,
                  fontFamily: F.mono,
                  letterSpacing: '0.18em',
                  color: C.muted,
                  marginBottom: 8,
                }}
              >
                OVERALL COMPRESSION RATIO
              </div>
            </HelpBubble>
            <EngineTooltip
              content={
                <div style={{ maxWidth: 300 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: C.text }}>
                    Engineering Compression
                  </div>
                  <div style={{ lineHeight: 1.6 }}>
                    Across all active projects, your AI-augmented development delivers the equivalent of{' '}
                    <span style={{ color: C.violet, fontFamily: F.mono }}>
                      {(data.totalEquivalentWeeks ?? 0).toFixed(0)} engineering-weeks
                    </span>{' '}
                    in{' '}
                    <span style={{ color: C.textSoft, fontFamily: F.mono }}>
                      {(data.totalActualHours ?? 0).toFixed(0)} actual hours
                    </span>
                    . Traditional replacement cost:{' '}
                    <span style={{ color: C.neon, fontFamily: F.mono }}>
                      {formatMoney(data.totalReplacementCost ?? 0)}
                    </span>
                    .
                  </div>
                </div>
              }
            >
              <NeonValue color={C.neon} size={64} glow>
                {countCompression}×
              </NeonValue>
            </EngineTooltip>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>
              <span style={{ fontFamily: F.mono, color: C.textSoft }}>{data.activeProjects}</span> active
              project{data.activeProjects !== 1 ? 's' : ''} tracked
            </div>
          </div>

          {/* Right — key value props */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flexShrink: 0 }}>
            <EngineTooltip content={<div>Market-rate cost to build everything tracked in ForgeTrack.</div>}>
              <div style={{ textAlign: 'right' }}>
                <HelpBubble helpKey="dashboard.replacement_cost" indicatorPosition="inline-end">
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em' }}>
                    REPLACEMENT COST
                  </div>
                </HelpBubble>
                <div style={{ fontSize: 28, fontFamily: F.mono, fontWeight: 700, color: C.neon, textShadow: `0 0 16px ${C.neonGlow}` }}>
                  {formatMoney(countReplCost)}
                </div>
              </div>
            </EngineTooltip>
            <EngineTooltip content={<div>Total equivalent engineering-weeks across all projects.</div>}>
              <div style={{ textAlign: 'right' }}>
                <HelpBubble helpKey="dashboard.equiv_weeks" indicatorPosition="inline-end">
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em' }}>
                    EQUIV. DELIVERED
                  </div>
                </HelpBubble>
                <div style={{ fontSize: 22, fontFamily: F.mono, fontWeight: 700, color: C.violet }}>
                  {countEquivWeeks}<span style={{ fontSize: 11, color: C.dim }}> wks</span>
                </div>
              </div>
            </EngineTooltip>
            <EngineTooltip content={<div>Actual hours logged across all sessions.</div>}>
              <div style={{ textAlign: 'right' }}>
                <HelpBubble helpKey="dashboard.actual_hours" indicatorPosition="inline-end">
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em' }}>
                    ACTUAL INVESTED
                  </div>
                </HelpBubble>
                <div style={{ fontSize: 22, fontFamily: F.mono, fontWeight: 700, color: C.textSoft }}>
                  {countHours}<span style={{ fontSize: 11, color: C.dim }}> hrs</span>
                </div>
              </div>
            </EngineTooltip>
          </div>
        </div>

        {/* Mini velocity sparkline bar at bottom */}
        <div
          style={{
            height: 32,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 2,
            padding: '0 36px 8px',
            borderTop: `1px solid ${C.faint}`,
          }}
        >
          {velocitySpark.map((pt, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: `${(pt.y / 50) * 100}%`,
                background: `linear-gradient(180deg, ${C.neon}40, ${C.neon}10)`,
                borderRadius: '2px 2px 0 0',
                opacity: 0,
                animation: `engine-feed-in 0.3s ease ${0.8 + i * 0.03}s forwards`,
              }}
            />
          ))}
        </div>
      </GlassPanel>
      </ErrorBoundary>

      {/* ══════════ STAT CARDS ROW ══════════ */}
      <ErrorBoundary label="Metrics">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <HelpBubble helpKey="dashboard.active_projects">
          <MetricCard
            label="Projects" value={data.totalProjects} icon="📊" color={C.cyan}
            subtitle={`${data.activeProjects} active`}
            tooltip={<div>Total R&D projects in this tenant.</div>}
          />
        </HelpBubble>
        <HelpBubble helpKey="dashboard.total_work_items">
          <MetricCard
            label="Work Items" value={countWorkItems} icon="📋" color={C.violet}
            subtitle={`${data.completedWorkItems} completed`}
            tooltip={<div>Discrete engineering tasks across all projects, each with complexity scoring.</div>}
          />
        </HelpBubble>
        <HelpBubble helpKey="dashboard.total_sessions">
          <MetricCard
            label="Sessions" value={countSessions} icon="🎯" color={C.amber}
            tooltip={<div>Individual development sessions logged — human + AI collaboration time.</div>}
          />
        </HelpBubble>
        <HelpBubble helpKey="dashboard.total_decisions">
          <MetricCard
            label="Decisions" value={countDecisions} icon="🧭" color={C.blue}
            tooltip={<div>Architectural decision records captured during development.</div>}
          />
        </HelpBubble>
        <HelpBubble helpKey="dashboard.total_contributors">
          <MetricCard
            label="Contributors" value={countContributors} icon="👥" color={C.neon}
            tooltip={<div>Human developers and AI agents tracked across projects.</div>}
          />
        </HelpBubble>
      </div>
      </ErrorBoundary>

      {/* ══════════ TWO-COLUMN: Projects + Recent Activity ══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Left — Project summaries */}
        <ErrorBoundary label="Project Portfolio">
        <div>
          <SectionLabel color={C.cyan}>Project Portfolio</SectionLabel>
          <GlassPanel accent={C.cyan} delay={0.5} style={{ padding: 0 }}>
            {data.projectSummaries && data.projectSummaries.length > 0 ? (
              <div>
                {data.projectSummaries.map((proj, i) => (
                  <Link
                    key={proj.id}
                    to="/projects/$projectId"
                    params={{ projectId: String(proj.id) }}
                    style={{ textDecoration: 'none', display: 'block' }}
                  >
                    <ProjectSummaryRow project={proj} index={i} />
                  </Link>
                ))}
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}>🏗</div>
                <div style={{ fontSize: 13, color: C.muted }}>No projects yet</div>
                <Link
                  to="/projects"
                  style={{
                    display: 'inline-block',
                    marginTop: 12,
                    fontSize: 11,
                    fontFamily: F.mono,
                    color: C.neon,
                    background: `${C.neon}10`,
                    border: `1px solid ${C.neon}25`,
                    padding: '6px 16px',
                    borderRadius: 6,
                    textDecoration: 'none',
                  }}
                >
                  CREATE FIRST PROJECT →
                </Link>
              </div>
            )}
          </GlassPanel>
        </div>
        </ErrorBoundary>

        {/* Right — Recent sessions feed */}
        <ErrorBoundary label="Recent Activity">
        <div>
          <SectionLabel color={C.amber}>Recent Activity</SectionLabel>
          <GlassPanel accent={C.amber} delay={0.6} style={{ padding: 0 }}>
            {data.recentSessions && data.recentSessions.length > 0 ? (
              <div>
                {data.recentSessions.slice(0, 8).map((session, i) => (
                  <SessionFeedItem key={session.id} session={session} index={i} />
                ))}
                <Link
                  to="/sessions"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '10px 0',
                    borderTop: `1px solid ${C.faint}`,
                    fontSize: 11,
                    fontFamily: F.mono,
                    color: C.amber,
                    textDecoration: 'none',
                    letterSpacing: '0.08em',
                  }}
                >
                  VIEW ALL SESSIONS →
                </Link>
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}>🎯</div>
                <div style={{ fontSize: 13, color: C.muted }}>No sessions recorded yet</div>
              </div>
            )}
          </GlassPanel>
        </div>
        </ErrorBoundary>
      </div>
    </EnginePageShell>
  );
}

// ─── ProjectSummaryRow ──────────────────────────────────────────────────────

function ProjectSummaryRow({
  project,
  index,
}: {
  project: {
    id: number;
    name: string;
    status: string;
    compressionRatio: number;
    totalActualHours: number;
    totalEquivalentWeeks: number;
    replacementCostEstimate: number;
    workItemCount: number;
  };
  index: number;
}) {
  const [hovered, setHovered] = useState(false);
  const statusColor =
    project.status === 'Active' ? C.neon
    : project.status === 'Completed' ? C.cyan
    : project.status === 'Planning' ? C.violet
    : C.dim;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 18px',
        borderBottom: `1px solid ${C.faint}`,
        background: hovered ? 'rgba(16,28,52,0.4)' : 'transparent',
        transition: 'background 0.15s',
        cursor: 'pointer',
        opacity: 0,
        animation: `engine-feed-in 0.3s ease ${0.6 + index * 0.06}s forwards`,
      }}
    >
      {/* Status dot + name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PulseDot color={statusColor} size={6} />
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: F.display }}>
            {project.name}
          </span>
        </div>
        <div style={{ fontSize: 10, color: C.dim, marginTop: 3, fontFamily: F.mono }}>
          {project.workItemCount} items · {(project.totalActualHours ?? 0).toFixed(0)} hrs
        </div>
      </div>

      {/* Compression */}
      <EngineTooltip content={<div>Compression ratio for {project.name}</div>}>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontFamily: F.mono, fontWeight: 700, color: C.amber }}>
            {(project.compressionRatio ?? 0) > 0 ? `${(project.compressionRatio ?? 0).toFixed(1)}×` : '—'}
          </div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>
            {(project.totalEquivalentWeeks ?? 0).toFixed(0)} equiv wks
          </div>
        </div>
      </EngineTooltip>

      {/* Arrow */}
      <span
        style={{
          color: C.dim,
          fontSize: 14,
          opacity: hovered ? 1 : 0.3,
          transform: hovered ? 'translateX(3px)' : 'none',
          transition: 'all 0.15s',
        }}
      >
        →
      </span>
    </div>
  );
}

// ─── SessionFeedItem ────────────────────────────────────────────────────────

function SessionFeedItem({ session, index }: { session: any; index: number }) {
  const timeAgo = useMemo(() => {
    const diff = Date.now() - new Date(session.createdDate || session.startTime).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }, [session]);

  const hasAI = session.aiModel || session.aiTokensUsed > 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 18px',
        borderBottom: `1px solid ${C.faint}`,
        opacity: 0,
        animation: `engine-feed-in 0.3s ease ${0.7 + index * 0.06}s forwards`,
      }}
    >
      {/* Activity icon */}
      <span style={{ fontSize: 14, marginTop: 2 }}>
        {hasAI ? '🤖' : '👤'}
      </span>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.text }}>
          <span style={{ fontWeight: 600 }}>{session.contributorName || 'Unknown'}</span>
          <span style={{ color: C.muted }}> worked on </span>
          <span style={{ fontWeight: 500, color: C.cyan }}>{session.workItemName || 'Work Item'}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10, fontFamily: F.mono, color: C.dim }}>
          <span>{session.durationMinutes}min</span>
          {hasAI && (
            <span style={{ color: C.violet }}>
              {session.aiModel} · {(session.aiTokensUsed || 0).toLocaleString()} tokens
            </span>
          )}
          {session.projectName && <span>📂 {session.projectName}</span>}
        </div>
      </div>

      {/* Time */}
      <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, flexShrink: 0, marginTop: 2 }}>
        {timeAgo}
      </span>
    </div>
  );
}

// ─── Loading Skeleton ───────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <>
      <GlassPanel delay={0.1} style={{ marginBottom: 20, height: 180 }}>
        <div style={{ display: 'flex', gap: 30, alignItems: 'center', height: '100%' }}>
          <LoadingShimmer width={120} height={120} />
          <div style={{ flex: 1 }}>
            <LoadingShimmer width="30%" height={14} />
            <div style={{ height: 12 }} />
            <LoadingShimmer width="50%" height={48} />
          </div>
        </div>
      </GlassPanel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <GlassPanel key={i} delay={0.2 + i * 0.05}>
            <LoadingShimmer height={14} width="50%" />
            <div style={{ height: 8 }} />
            <LoadingShimmer height={28} width="40%" />
          </GlassPanel>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <GlassPanel delay={0.5}><LoadingShimmer height={200} /></GlassPanel>
        <GlassPanel delay={0.6}><LoadingShimmer height={200} /></GlassPanel>
      </div>
    </>
  );
}


